/**
 * 端末情報のStateを扱うモジュール
 */
import { ThunkDispatch } from 'redux-thunk';
import { AppState } from 'pages';
import {
  beginFetch,
  endFetch,
  failedFetch,
  FetchStatus
} from 'common/modules/app/fetch';
import {
  fetchFailure,
  ErrorResponse,
  clearErrors
} from 'common/modules/app/errors';
import { ActionsUnion, createAction } from 'common/utils/action-utils';
import {
  getTerminalsActivationRequest,
  postActivationTerminalsCancelRequest
} from 'common/utils/request/sas-request';
import { GetTerminalsActivationResponse } from 'common/utils/request/sas-response';
import { request } from 'common/utils/request/http-request';
import { getLogin } from 'common/modules/domain/user';
import { LOGIN_USER } from 'common/modules/domain/user';
import { InnerError } from '../app/innerErrorCode';
import { BoardStatus, setStatus } from 'common/modules/app/board';

enum ActionTypes {
  RESET_STATE = 'domain/terminal/RESET_STATE',
  FETCH_SUCCESS = 'domain/terminal/FETCH_SUCCESS',
  DELETE_SUCCESS = 'domain/terminal/DELETE_SUCCESS',
  CHANGE_SETTING = 'domain/terminal/CHANGE_SETTING'
}

export enum StateType {
  // 有効
  TYPE_ACTIVE = 'active',
  // 無効
  TYPE_DEACTIVE = 'deactive'
}

export const GET_TERMINALS_ACTIVATION_ID = 'get_terminals_activation';
export const DELETE_TERMINAL_ACTIVATION_ID = 'delete_terminal_activation';

/**
 * 端末情報をクリアする
 */
export const resetState = () => createAction(ActionTypes.RESET_STATE);

/**
 * 端末情報を置き換える
 */
export const fetchSuccess = (payload: TerminalInformation[]) => {
  // TerminalInformation[]をTerminalStateに変換してActionを作成
  let terminalState: TerminalState = {};
  for (const data of payload) {
    // 既に同じユーザー購入番号のデータが存在するか確認
    if (data.purchaseId in terminalState) {
      // 存在する場合
      terminalState = {
        ...terminalState,
        [data.purchaseId]: {
          ...terminalState[data.purchaseId],
          [data.terminalId]: {
            terminalInfo: data,
            isDelete: false
          }
        }
      };
    } else {
      // 存在しない場合
      terminalState = {
        ...terminalState,
        [data.purchaseId]: {
          [data.terminalId]: {
            terminalInfo: data,
            isDelete: false
          }
        }
      };
    }
  }
  return createAction(ActionTypes.FETCH_SUCCESS, terminalState);
};

/**
 * 端末情報を削除(ユーザー購入番号単位)
 * @param purchaseId 購入ID
 */
export const deleteSuccess = (purchaseId: string) => {
  return createAction(ActionTypes.DELETE_SUCCESS, { purchaseId: purchaseId });
};

/**
 * 端末情報を削除(端末ID単位)
 * @param purchaseId 購入ID
 * @param terminalIds 端末ID
 */
export const deleteSuccessTerminalId = (
  purchaseId: string,
  terminalIds: string[]
) => {
  return createAction(ActionTypes.DELETE_SUCCESS, {
    purchaseId: purchaseId,
    terminalIds: terminalIds
  });
};

/**
 * 削除対象か否かのフラグを更新
 * @param purchaseId 購入ID
 * @param terminalId 端末ID
 * @param isDelete 削除フラグ
 */
export const changeSetting = (
  purchaseId: string,
  terminalId: string,
  isDelete: boolean
) => {
  return createAction(ActionTypes.CHANGE_SETTING, {
    purchaseId: purchaseId,
    terminalId: terminalId,
    isDelete: isDelete
  });
};

const actions = {
  resetState,
  fetchSuccess,
  deleteSuccess,
  changeSetting,
  deleteSuccessTerminalId
};
type TerminalAction = ActionsUnion<typeof actions>;

/**
 * 端末情報一覧を取得する
 * @param token: SASが発行した独自トークン
 * @param purchaseId: 購入番号
 */
export const getTerminalsActivation = (token: string, purchaseId: string) => {
  return async (
    dispatch: ThunkDispatch<Record<string, unknown>, void, any>,
    getState: () => AppState
  ) => {
    // キャンセルを判断する処理を定義
    const isCancel = () => {
      const status = getState().app.fetch[GET_TERMINALS_ACTIVATION_ID];
      return status === FetchStatus.CANCEL;
    };
    // GET_TERMINALS_ACTIVATION_IDを処理中に変更
    dispatch(beginFetch(GET_TERMINALS_ACTIVATION_ID));

    // ErrorStateが端末情報取得だった場合はリセットする
    if (getState().app.error.requestId === GET_TERMINALS_ACTIVATION_ID) {
      dispatch(clearErrors());
    }

    let nextKey = '';
    do {
      // 端末情報一覧取得APIを実行
      const requestData = getTerminalsActivationRequest(
        token,
        purchaseId,
        nextKey
      );
      const response = await request(
        requestData.url,
        requestData.config,
        false,
        false
      );
      // API実行結果がエラーかどうか判断
      if (response.error) {
        // エラー処理
        const error = response.error as ErrorResponse;
        error.requestId = GET_TERMINALS_ACTIVATION_ID;
        if (
          error.statusCode &&
          (error.statusCode === 401 || error.statusCode === 403)
        ) {
          // 認証エラーの場合は再ログイン
          await dispatch(getLogin());
          // 再ログインに失敗した場合はエラー
          const status = getState().app.fetch[LOGIN_USER];
          if (status === FetchStatus.FAILED) {
            const innerError = InnerError.loginFailed;
            innerError.requestId = GET_TERMINALS_ACTIVATION_ID;
            dispatch(fetchFailure(innerError));
            dispatch(failedFetch(GET_TERMINALS_ACTIVATION_ID));
          }
          return;
        } else {
          dispatch(fetchFailure(error));
          dispatch(failedFetch(GET_TERMINALS_ACTIVATION_ID));
          return;
        }
      }
      // APIが成功した場合は取得した端末情報でstateを更新
      const body: GetTerminalsActivationResponse = (response.body as unknown) as GetTerminalsActivationResponse;
      // 取得した端末情報でstateを更新
      dispatch(fetchSuccess(body.terminals));
      // nextKeyの有無、キャンセルの有無を判断
      if (body.nextKey === '' || isCancel()) {
        // キャンセル又は次の取得が無いため処理を抜ける
        nextKey = '';
      } else {
        // nextKeyを更新
        nextKey = body.nextKey;
      }
    } while (nextKey);
    // GET_TERMINALS_ACTIVATION_IDを未処理に変更
    dispatch(endFetch(GET_TERMINALS_ACTIVATION_ID));
  };
};

/**
 * 端末情報を削除する
 * @param token: SASが発行した独自トークン
 * @param purchaseId: 購入番号
 * @param terminalIds: 削除対象の端末IDリスト
 */
export const deleteTerminalActivation = (
  token: string,
  purchaseId: string,
  terminalIds: string[]
) => {
  return async (
    dispatch: ThunkDispatch<Record<string, unknown>, void, any>,
    getState: () => AppState
  ) => {
    // DELETE_TERMINAL_ACTIVATION_IDを処理中に変更
    dispatch(beginFetch(DELETE_TERMINAL_ACTIVATION_ID));

    // ErrorStateが端末情報削除だった場合はリセットする
    if (getState().app.error.requestId === DELETE_TERMINAL_ACTIVATION_ID) {
      dispatch(clearErrors());
    }
    // 削除対象の端末IDリストを10件ずつに分割する
    const sliceByNumber = (ids: string[], cnt: number) => {
      // 配列の要素数をn件ずつ分割した場合の要素数を算出
      const length = Math.ceil(ids.length / cnt);
      // 元の配列からn件ずつに分割する
      const tmp_list: string[][] = [];
      for (let i = 0; i < length; i++) {
        tmp_list.push(ids.slice(i * cnt, (i + 1) * cnt));
      }
      // 分割後のリストを返す
      return tmp_list;
    };
    const targetIds = sliceByNumber(terminalIds, 10);
    for (const ids of targetIds) {
      // 端末情報削除APIを実行
      const requestData = postActivationTerminalsCancelRequest(
        token,
        ids,
        purchaseId
      );
      const response = await request(
        requestData.url,
        requestData.config,
        false,
        false
      );
      // API実行結果がエラーかどうか判断
      if (response.error) {
        // エラー処理
        const error = response.error as ErrorResponse;
        error.requestId = DELETE_TERMINAL_ACTIVATION_ID;
        dispatch(fetchFailure(error));
        dispatch(failedFetch(DELETE_TERMINAL_ACTIVATION_ID));
        // 削除失敗ダイアログを表示
        dispatch(setStatus(BoardStatus.TERMINAL_FAILURE_DISP));
        return;
      }
      // APIが成功した場合は対象の端末情報をstateから削除
      dispatch(deleteSuccessTerminalId(purchaseId, ids));
    }
    // 削除成功ダイアログを表示
    dispatch(setStatus(BoardStatus.TERMINAL_SUCCESS_DISP));
    // DELETE_TERMINAL_ACTIVATION_IDを未処理に変更
    dispatch(endFetch(DELETE_TERMINAL_ACTIVATION_ID));
  };
};

/**
 * 端末情報をクリアする
 */
export const resetTerminalsActivation = () => {
  return (dispatch: ThunkDispatch<Record<string, unknown>, void, any>) => {
    dispatch(resetState());
  };
};

/**
 * 端末情報
 */
export interface TerminalInformation {
  /** 端末ID */
  terminalId: string;
  /** ユーザー購入番号(SAS発行) */
  purchaseId: string;
  /** 端末名 */
  terminalName: string;
  /** 状態("active"：有効 or "deactive"：無効) */
  status: StateType;
  /** 対応するデバイス利用状況のインデックス */
  index: string;
  /** 登録日時 (ISO8601 UTC) */
  createAt: string;
}

export interface TerminalMap {
  [key: string]: {
    terminalInfo: TerminalInformation;
    isDelete: boolean;
  };
}

/**
 * 端末情報のState
 * キーにユーザー購入番号と端末ID、値に端末情報を持つ
 * {
 *   ユーザー購入番号: {
 *     端末ID: 端末情報
 *   }
 * }
 */
export interface TerminalState {
  [key: string]: TerminalMap;
}
const initialState: TerminalState = {};

export default (
  state: TerminalState = initialState,
  actions: TerminalAction
): TerminalState => {
  switch (actions.type) {
    case ActionTypes.FETCH_SUCCESS: {
      const modifiedState = { ...state };
      // 更新対象のユーザー購入番号を取得
      const up_keys = Object.keys(actions.payload);
      for (const key of up_keys) {
        // 既に同じユーザー購入番号のデータを更新
        modifiedState[key] = { ...modifiedState[key], ...actions.payload[key] };
      }
      return modifiedState;
    }
    case ActionTypes.RESET_STATE: {
      return initialState;
    }
    case ActionTypes.DELETE_SUCCESS: {
      const modifiedState = { ...state };
      const purchaseId = actions.payload.purchaseId;
      if ('terminalIds' in actions.payload) {
        // 端末ID単位で削除
        if (purchaseId in modifiedState) {
          // 指定されたユーザー購入番号のデータがある場合のみ削除
          for (const id of actions.payload.terminalIds) {
            delete modifiedState[purchaseId][id];
          }
        }
      } else {
        // ユーザー購入番号単位で削除
        delete modifiedState[purchaseId];
      }
      return modifiedState;
    }
    case ActionTypes.CHANGE_SETTING: {
      const modifiedState = { ...state };
      // 対象データを取り出し、isDeleteを変更する
      const purchaseId = actions.payload.purchaseId;
      const terminalId = actions.payload.terminalId;
      modifiedState[purchaseId][terminalId].isDelete = actions.payload.isDelete;
      return modifiedState;
    }
    default: {
      return state;
    }
  }
};
